/**
 * Copy a sensitive string to the clipboard and schedule an automatic
 * overwrite after `clearAfterMs` (default 60 seconds). If the clipboard
 * still holds exactly the same text at the scheduled time, it is
 * overwritten with an empty string. If the user already copied something
 * else, we leave it alone.
 *
 * SECURITY (F7): the browser clipboard has no OS-level expiration. Without
 * this helper, a mnemonic copied during wallet creation would sit there
 * indefinitely, readable by any subsequently opened site that requests
 * clipboard-read permission or by clipboard-sniffer malware. 60 seconds
 * is long enough to paste into a password manager or physical notebook,
 * short enough to bound the residency window.
 *
 * `readText()` can throw if the permission is denied or the tab loses
 * focus — in that case we silently do nothing (can't overwrite what we
 * can't verify without racing user copies).
 */
export async function copySecretWithAutoClear(
  secret: string,
  clearAfterMs = 60_000,
): Promise<void> {
  await navigator.clipboard.writeText(secret);
  setTimeout(async () => {
    try {
      const current = await navigator.clipboard.readText();
      if (current === secret) {
        await navigator.clipboard.writeText('');
      }
    } catch {
      // Permission denied or tab blurred — best-effort only.
    }
  }, clearAfterMs);
}
